import type { ApiError, ApiSuccess, ErrorCode } from '@docsystem/shared';
import { authStore, getDisplayToken } from './auth-store';

const API_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3001/api/v1';

export class ApiRequestError extends Error {
  readonly errorCode: ErrorCode | 'NETWORK_ERROR';
  readonly statusCode: number;
  readonly requestId?: string;
  readonly fieldErrors?: Array<{ field: string; message: string }>;
  /** Extra payload the server attaches so the UI can resync without a second call. */
  readonly data?: unknown;

  constructor(error: Partial<ApiError> & { message: string; statusCode: number }) {
    super(error.message);
    this.name = 'ApiRequestError';
    this.errorCode = (error.errorCode ?? 'NETWORK_ERROR') as ErrorCode | 'NETWORK_ERROR';
    this.statusCode = error.statusCode;
    this.requestId = error.requestId;
    this.fieldErrors = error.errors;
    this.data = error.data;
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Send the display credential instead of the staff access token. */
  asDisplay?: boolean;
  skipRefresh?: boolean;
}

// A single in-flight refresh, shared by every 401 that arrives while it runs —
// otherwise ten concurrent queries would each rotate the token and trip the
// server's refresh-reuse detection.
let refreshPromise: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) return false;

      const payload = (await response.json()) as ApiSuccess<{ accessToken: string }>;
      authStore.setAccessToken(payload.data.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers all observe this result.
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    }
  })();

  return refreshPromise;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, asDisplay, skipRefresh, headers, ...rest } = options;

  // A provisioned screen carries its own display token. When there isn't one but a
  // staff member is signed in — e.g. previewing via "Open display" — fall back to
  // their session so the page is usable without provisioning a kiosk first.
  const displayToken = asDisplay ? getDisplayToken() : null;
  const token = displayToken ?? authStore.accessToken;
  const usingDisplayToken = Boolean(displayToken);

  const isFormData = body instanceof FormData;

  const requestHeaders: Record<string, string> = {
    Accept: 'application/json',
    // FormData sets its own Content-Type, including the multipart boundary. Setting
    // it here would omit the boundary and the server would see an unparseable body.
    ...(body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...((headers as Record<string, string>) ?? {}),
  };

  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...rest,
      headers: requestHeaders,
      credentials: 'include',
      body: body === undefined ? undefined : isFormData ? body : JSON.stringify(body),
    });
  } catch {
    throw new ApiRequestError({
      message: 'Cannot reach the server. Check your connection.',
      statusCode: 0,
      errorCode: 'NETWORK_ERROR' as ErrorCode,
    });
  }

  // A stale access token is normal, not an error: refresh once and replay.
  // Display tokens are long-lived and have no refresh flow, so they skip this.
  if (response.status === 401 && !skipRefresh && !usingDisplayToken) {
    const refreshed = await refreshSession();
    if (refreshed) {
      return apiRequest<T>(path, { ...options, skipRefresh: true });
    }
    authStore.clear();
  }

  if (response.status === 204) return undefined as T;

  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {
    const error = (payload ?? {}) as Partial<ApiError>;
    throw new ApiRequestError({
      message: error.message ?? `Request failed (${response.status})`,
      statusCode: response.status,
      errorCode: error.errorCode,
      requestId: error.requestId,
      errors: error.errors,
      data: error.data,
    });
  }

  return (payload as ApiSuccess<T>).data;
}

export const api = {
  get: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  /** Multipart upload. Shares the auth + refresh path with every other request. */
  postForm: <T>(path: string, form: FormData, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'POST', body: form }),
  patch: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'PUT', body }),
  delete: <T>(path: string, options?: RequestOptions) =>
    apiRequest<T>(path, { ...options, method: 'DELETE' }),
};

export function buildQuery(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}
